// ./src/hooks/useToast.ts
import { useCallback } from "react";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconInfoCircle,
} from "@tabler/icons-react";
import React from "react";

export interface ToastOptions {
  /** Toast title */
  title?: string;
  /** Auto-close delay in ms (default: 5000, set to false to disable) */
  autoClose?: number | false;
  /** Whether the toast can be closed by clicking */
  withCloseButton?: boolean;
}

export interface UseToastReturn {
  /** Show a success toast */
  success: (message: string, options?: ToastOptions) => void;
  /** Show an error toast */
  error: (message: string, options?: ToastOptions) => void;
  /** Show a warning toast */
  warning: (message: string, options?: ToastOptions) => void;
  /** Show an info toast */
  info: (message: string, options?: ToastOptions) => void;
  /** Hide all toasts */
  hideAll: () => void;
}

const DEFAULT_AUTO_CLOSE = 3000;
const ERROR_AUTO_CLOSE = 8000;

export const useToast = (): UseToastReturn => {
  const success = useCallback((message: string, options?: ToastOptions) => {
    notifications.show({
      title: options?.title ?? "Success",
      message,
      color: "green",
      icon: React.createElement(IconCheck, { size: 18 }),
      autoClose: options?.autoClose ?? DEFAULT_AUTO_CLOSE,
      withCloseButton: options?.withCloseButton ?? true,
    });
  }, []);

  const error = useCallback((message: string, options?: ToastOptions) => {
    notifications.show({
      title: options?.title ?? "Error",
      message,
      color: "red",
      icon: React.createElement(IconX, { size: 18 }),
      autoClose: options?.autoClose ?? ERROR_AUTO_CLOSE,
      withCloseButton: options?.withCloseButton ?? true,
    });
  }, []);

  const warning = useCallback((message: string, options?: ToastOptions) => {
    notifications.show({
      title: options?.title ?? "Warning",
      message,
      color: "yellow",
      icon: React.createElement(IconAlertTriangle, { size: 18 }),
      autoClose: options?.autoClose ?? DEFAULT_AUTO_CLOSE,
      withCloseButton: options?.withCloseButton ?? true,
    });
  }, []);

  const info = useCallback((message: string, options?: ToastOptions) => {
    notifications.show({
      title: options?.title ?? "Info",
      message,
      color: "blue",
      icon: React.createElement(IconInfoCircle, { size: 18 }),
      autoClose: options?.autoClose ?? DEFAULT_AUTO_CLOSE,
      withCloseButton: options?.withCloseButton ?? true,
    });
  }, []);

  const hideAll = useCallback(() => {
    notifications.cleanQueue();
    notifications.clean();
  }, []);

  return {
    success,
    error,
    warning,
    info,
    hideAll,
  };
};
